import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App, Segmented } from 'antd'
import { useTranslation } from 'react-i18next'
import { api, type Invoke } from '../api'
import { useSession } from '@argo/common/session'
import { setLanguage } from '../i18n'
import { useTheme, type Mode } from '@argo/common/theme'
import { useAuth } from '@argo/common/auth'
import { config } from '@argo/common/config'
import ContactPanel from '@argo/common/components/ContactPanel'

export default function Settings() {
  const { t, i18n } = useTranslation()
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { account, setAccount, me } = useSession()
  const { mode, setMode } = useTheme()
  const { signOut } = useAuth()
  const operator = me?.roles.includes('operator') ?? false

  const pinStatus = useQuery({ queryKey: ['pin'], queryFn: api.pinStatus })
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [actAs, setActAs] = useState(account)
  const [note, setNote] = useState<{ text: string; kind: 'ok' | 'err' } | null>(null)

  const setPin = useMutation({
    mutationFn: () => api.setPin(next, current || undefined),
    onSuccess: () => {
      setNote({ text: t('done'), kind: 'ok' })
      setCurrent(''); setNext('')
      qc.invalidateQueries({ queryKey: ['pin'] })
      qc.invalidateQueries({ queryKey: ['me'] })
    },
    onError: (e: Error) => setNote({ text: e.message, kind: 'err' }),
  })

  const meta = useMutation({
    mutationFn: (v: { locus: string; name: string; repo: string }) =>
      api.command<Invoke>('tuna', 'setmeta', v.locus, v),
    onSuccess: () => message.success(t('done')),
    onError: (e: Error) => message.error(e.message),
  })

  const submitPin = (e: FormEvent) => {
    e.preventDefault()
    setNote(null)
    if (!/^\d{4,8}$/.test(next)) { setNote({ text: t('pinHint'), kind: 'err' }); return }
    setPin.mutate()
  }

  const lang = i18n.language.startsWith('mn') ? 'mn' : 'en'

  return (
    <div className="page page-mid cols cols-2">
      <div className="panel">
        <div className="panel-head"><span className="grow">{t('account')}</span></div>
        <div className="panel-body flush">
          <div className="list-row">
            <div className="grow"><div className="title">{me?.username || '—'}</div><div className="sub">{t('username')}</div></div>
            <div className="row" style={{ gap: 4 }}>{(me?.roles ?? []).map(r => <span key={r} className="chip">{r}</span>)}</div>
          </div>
          <div className="list-row">
            <div className="grow"><div className="title">{account || '—'}</div><div className="sub">{t('account')}</div></div>
          </div>
          {config.auth !== 'off' && (
            <div className="list-row">
              <div className="grow"><div className="sub">{t('signOut')}</div></div>
              <button className="icon-btn" style={{ width: 'auto', padding: '0 12px' }} onClick={signOut}>{t('signOut')}</button>
            </div>
          )}
        </div>
        {operator && (
          <div className="panel-body" style={{ borderTop: '1px solid var(--border)' }}>
            <div className="field">
              <label htmlFor="actas">{t('actAs')}</label>
              <div className="input-wrap">
                <input id="actas" value={actAs} placeholder="alice" style={{ fontWeight: 400 }}
                  autoCapitalize="none" onChange={e => setActAs(e.target.value)} />
              </div>
            </div>
            <button className="submit buy" onClick={() => setAccount(actAs.trim())}>{t('save')}</button>
          </div>
        )}
      </div>

      <ContactPanel />

      <div className="panel">
        <div className="panel-head"><span className="grow">{t('txPin')}</span>
          <span className={`chip ${pinStatus.data?.set ? 'up' : ''}`}>{pinStatus.data?.set ? t('pinSet') : t('pinNotSet')}</span>
        </div>
        <div className="panel-body">
          <p className="muted" style={{ marginTop: 0, fontSize: 12.5 }}>{t('pinHint')}</p>
          <form onSubmit={submitPin}>
            {pinStatus.data?.set && (
              <div className="field">
                <label htmlFor="cur">{t('currentPin')}</label>
                <div className="input-wrap">
                  <input id="cur" type="password" inputMode="numeric" autoComplete="off" placeholder="••••"
                    value={current} onChange={e => setCurrent(e.target.value.replace(/[^\d]/g, ''))} />
                </div>
              </div>
            )}
            <div className="field">
              <label htmlFor="new">{t('newPin')}</label>
              <div className="input-wrap">
                <input id="new" type="password" inputMode="numeric" autoComplete="off" placeholder="••••"
                  value={next} onChange={e => setNext(e.target.value.replace(/[^\d]/g, ''))} />
              </div>
            </div>
            <button className="submit buy" type="submit" disabled={setPin.isPending}>
              {setPin.isPending ? t('loading') : t('save')}
            </button>
            {note && <p className={`form-note ${note.kind}`}>{note.text}</p>}
            {pinStatus.data?.locked_until && (
              <p className="form-note err">{t('errLocked')} ({new Date(pinStatus.data.locked_until).toLocaleTimeString()})</p>
            )}
          </form>
        </div>
      </div>

      <div className="panel">
        <div className="panel-head"><span className="grow">{t('theme')} · {t('language')}</span></div>
        <div className="panel-body stack">
          <Segmented
            block value={mode} onChange={v => setMode(v as Mode)}
            options={[
              { value: 'light', label: t('light') },
              { value: 'dark', label: t('dark') },
              { value: 'system', label: t('system') },
            ]}
          />
          <Segmented
            block value={lang} onChange={v => setLanguage(v as 'mn' | 'en')}
            options={[{ value: 'mn', label: 'Монгол' }, { value: 'en', label: 'English' }]}
          />
        </div>
      </div>

      {operator && (
        <div className="panel">
          <div className="panel-head"><span className="grow">{t('meta')}</span><span className="chip">operator</span></div>
          <div className="panel-body">
            <form onSubmit={e => {
              e.preventDefault()
              const f = new FormData(e.currentTarget)
              meta.mutate({
                locus: String(f.get('locus') ?? ''),
                name: String(f.get('name') ?? ''),
                repo: String(f.get('repo') ?? ''),
              })
            }}>
              <div className="field">
                <label htmlFor="locus">{t('locus')}</label>
                <div className="input-wrap"><input id="locus" name="locus" placeholder="alice" style={{ fontWeight: 400 }} /></div>
              </div>
              <div className="field">
                <label htmlFor="mname">{t('name')}</label>
                <div className="input-wrap"><input id="mname" name="name" style={{ fontWeight: 400 }} /></div>
              </div>
              <div className="field">
                <label htmlFor="mrepo">{t('repo')}</label>
                <div className="input-wrap"><input id="mrepo" name="repo" style={{ fontWeight: 400 }} /></div>
              </div>
              <button className="submit buy" type="submit" disabled={meta.isPending}>{t('save')}</button>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
